/**
 * 系统备份 — GET/POST /api/admin/backups
 *
 * POST 立即返回一个 status='running' 的行，pg_dump 后台运行完成后会把
 * 该行的 status 翻成 'ok' 或 'error'。页面默认 5 秒轮询一次以反映状态。
 */
import { Button, Popconfirm, Space, Table, Tag, Tooltip, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface BackupRow {
  id: number;
  filename: string;
  size_bytes: string;
  status: 'running' | 'ok' | 'error';
  created_by: number;
  error_msg: string | null;
  create_time: string;
  finish_time: string | null;
}

function formatSize(bytes: string): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function statusTag(s: BackupRow['status']) {
  if (s === 'running') return <Tag color="blue">进行中</Tag>;
  if (s === 'ok') return <Tag color="green">成功</Tag>;
  return <Tag color="red">失败</Tag>;
}

export function BackupPage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('system.backup.create');

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'backups'],
    queryFn: () => api.get<BackupRow[]>('/api/admin/backups'),
    refetchInterval: 5000,
  });

  async function onCreate() {
    try {
      await api.post('/api/admin/backups');
      message.success('备份任务已提交，正在后台执行');
      qc.invalidateQueries({ queryKey: ['admin', 'backups'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '触发失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '文件名',
      dataIndex: 'filename',
      ellipsis: true,
      render: (v: string) => <code style={{ fontSize: 12 }}>{v}</code>,
    },
    {
      title: '大小',
      dataIndex: 'size_bytes',
      width: 110,
      render: formatSize,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: BackupRow['status'], row: BackupRow) =>
        v === 'error' && row.error_msg ? (
          <Tooltip title={row.error_msg}>{statusTag(v)}</Tooltip>
        ) : (
          statusTag(v)
        ),
    },
    { title: '发起人', dataIndex: 'created_by', width: 90 },
    { title: '开始时间', dataIndex: 'create_time', width: 170 },
    {
      title: '完成时间',
      dataIndex: 'finish_time',
      width: 170,
      render: (v: string | null) => v ?? '—',
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>系统备份</h2>
      <Space style={{ marginBottom: 12 }}>
        {canCreate && (
          <Popconfirm
            title="确定要立即执行 pg_dump 备份？"
            description="备份将在后台异步执行，期间不会阻塞请求。"
            onConfirm={onCreate}
          >
            <Button type="primary">触发新备份</Button>
          </Popconfirm>
        )}
        <Button onClick={() => qc.invalidateQueries({ queryKey: ['admin', 'backups'] })}>
          刷新
        </Button>
      </Space>

      <Table<BackupRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={{ x: 1000 }}
      />
    </div>
  );
}
